import React from "react";
import { getContainer } from "../../ioc/IOCSetup";
import { ListRequest, UserBonusStatus, UserBonusView, UserLevelAdminView, PermissionCategory } from "../../http/protocol";
import { EntityDataTable } from "../list/list/EntityDataTable";
import { ColumnType } from "../list/list/ColumnType";
import { ActionType } from "../list/list/DataTable";
import { UserBonusService } from "../../services/UserBonusService";
import { Link } from "react-router-dom";
import { t } from "../../i18n";
import { UserService } from "../../services/UserService";
import { Collapsible } from "../Collapsible";
import { PermissionRequired } from "../security/PermissionRequired";

interface State {
  userLevel?: UserLevelAdminView;
  editing?: boolean;
  xpInput?: string;
  saving?: boolean;
  error?: string;
}

interface Props {
  userId: number;
}

export class UserLevelComponent extends React.Component<Props, State> {
  userService: UserService;

  constructor(props: Props) {
    super(props);
    this.userService = getContainer().getUserService();
    this.state = {};
  }

  async componentDidMount() {
    const level = await this.userService.getUserLevel(this.props.userId);
    this.setState({ userLevel: level });
  }

  startEdit() {
    this.setState({
      editing: true,
      xpInput: String(this.state.userLevel?.currentXp ?? 0),
      error: undefined,
    });
  }

  cancelEdit() {
    this.setState({ editing: false, error: undefined });
  }

  async saveXp() {
    const input = this.state.xpInput ?? "";
    if (input.trim() === "") {
      this.setState({ error: t("userLevelComponent.invalidXp", undefined, "Please enter a valid number") });
      return;
    }
    const parsed = Number(input);
    if (Number.isNaN(parsed) || !Number.isInteger(parsed)) {
      this.setState({ error: t("userLevelComponent.invalidXp", undefined, "Please enter a valid number") });
      return;
    }
    this.setState({ saving: true, error: undefined });
    try {
      const updated = await this.userService.setUserXp(this.props.userId, parsed);
      this.setState({ userLevel: updated, editing: false, saving: false });
    } catch (e: any) {
      this.setState({ error: e?.message || t("userLevelComponent.saveFailed", undefined, "Failed to save XP"), saving: false });
    }
  }

  renderXpCell(level: UserLevelAdminView) {
    if (this.state.editing) {
      return (
        <td>
          <input
            type="number"
            className="form-control"
            value={this.state.xpInput ?? ""}
            onChange={(e) => this.setState({ xpInput: e.target.value })}
          />
          <small className="form-text text-muted">{t("userLevelComponent.xpAppliedOnWager", undefined, "Level will be applied upon next player wager")}</small>
          <div className="mt-1">
            <button
              className="btn btn-success btn-sm"
              onClick={() => this.saveXp()}
              disabled={this.state.saving}
            >
              {t("button.save", undefined, "Save")}
            </button>
            <button
              className="btn btn-secondary btn-sm ml-1"
              onClick={() => this.cancelEdit()}
              disabled={this.state.saving}
            >
              {t("button.cancel", undefined, "Cancel")}
            </button>
          </div>
          {this.state.error && <small className="text-danger">{this.state.error}</small>}
        </td>
      );
    }
    return (
      <td>
        {level.nextLevelXp == null
          ? t("userLevelComponent.maxLevel", undefined, "MAX")
          : `${level.currentXp} / ${level.nextLevelXp} (${level.nextLevelProgressionPercentage}%)`}
        <PermissionRequired
          permissions={[PermissionCategory.USERS]}
          accessLevel="w"
          showError={false}
        >
          <button
            className="btn btn-link btn-sm"
            onClick={() => this.startEdit()}
          >
            {t("button.edit", undefined, "Edit")}
          </button>
        </PermissionRequired>
      </td>
    );
  }

  render() {
    const level = this.state.userLevel;

    return (
      <div className="col-lg-12">
        <Collapsible
          title={t(`title.userLevel`)}
          collapsed={true}
        >
          {level && (
            <table className="table table-striped">
              <tbody>
                <tr>
                  <th>{t("userLevelComponent.level")}</th>
                  <td>{level.currentLevelName ?? "N/A"}</td>
                </tr>
                <tr>
                  <th>{t("userLevelComponent.xpProgression")}</th>
                  {this.renderXpCell(level)}
                </tr>
                <tr>
                  <th>{t("userLevelComponent.nextLevel")}</th>
                  <td>{level.nextLevelName}</td>
                </tr>
              </tbody>
            </table>
          )}
        </Collapsible>
      </div>
    );
  }
}

export default UserLevelComponent;
